import { useState } from 'react';
import { nanoid } from 'nanoid';
import { sendEmailWithInvoices } from 'Shared/util/web-api';
import { useLocalNotificationBanner, useTranslation } from 'Shared/util/custom-hooks';
import { joinClassNames as c } from 'Shared/util/styles';
import DragAndDrop from 'Shared/components/drag-and-drop';
import Description from 'Shared/components/description';
import ProgressBar from 'Shared/components/progress-bar';
import InlineSvg from 'Shared/components/inline-svg';
import Switch from 'Shared/components/switch';
import Textarea from 'Shared/components/textarea';
import Button from 'Shared/components/button';
import { getProgressBarSizeAndPercentage, exceedsTotalAllowedFileSize } from './submit-invoices-utils';
import styles from './submit-invoices.css';


const renderNotAcceptedFileMessage = (files, tnb) => (
  <div className={styles.notAcceptedFiles}>
    {tnb.file_type_not_accepted}
    {/* eslint-disable-next-line react/no-array-index-key */}
    {files.map((f, i) => <span key={i}>{`• ${f.name}`}</span>)}
  </div>
);

const renderFiles = (files, onRemoveFile, disabled) => (
  <div className={styles.filesContainer}>
    {files.map((file) => (
      <div className={styles.fileItem} key={file.id}>
        <div className={styles.fileItemText}>
          {file.name}
        </div>
        <div
          className={c(styles.fileItemRemove, (disabled && styles.disabled))}
          onClick={!disabled ? onRemoveFile : null}
          data-fileid={file.id}
        >
          <InlineSvg src="/close.svg" />
        </div>
      </div>
    ))}
  </div>
);

const ACCEPTED_FILE_TYPES = ['.pdf'];


const SubmitInvoices = () => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [expressTransfer, setExpressTransfer] = useState(false);
  const [transferMessage, setTransferMessage] = useState('');
  const {
    NotificationBannerType,
    addLocalNotificationBanner,
    removeAllLocalNotificationBanners,
  } = useLocalNotificationBanner();
  const t = useTranslation(x => x.manual.submit_invoices);

  const handleAddFiles = (addedFiles) => {
    if (!addedFiles) return;

    removeAllLocalNotificationBanners();

    const {
      accepted: acceptedFiles,
      notAccepted: notAcceptedFiles,
    } = addedFiles;

    if (notAcceptedFiles.length > 0) {
      addLocalNotificationBanner((tnb) => renderNotAcceptedFileMessage(notAcceptedFiles, tnb), NotificationBannerType.Attention);
    }

    if (!exceedsTotalAllowedFileSize(acceptedFiles, files)) {
      acceptedFiles.map((obj) => {
        obj.id = nanoid();
        return obj;
      });
      setFiles((prev) => prev.concat(acceptedFiles));
    } else {
      addLocalNotificationBanner((tnb) => tnb.files_were_not_added, NotificationBannerType.Attention);
    }
  };

  const handleRemoveFile = (e) => {
    const fileId = e.currentTarget.dataset.fileid;
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
    removeAllLocalNotificationBanners();
  };

  const handleSubmitFiles = () => {
    if (!files || !files.length > 0) return;

    setLoading(true);
    removeAllLocalNotificationBanners();

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('useExpressTransfer', expressTransfer);
    formData.append('transferMessage', transferMessage);

    sendEmailWithInvoices(formData)
      .then(() => {
        setFiles([]);
        setExpressTransfer(false);
        setTransferMessage('');
        addLocalNotificationBanner((tnb) => tnb.your_invoices_have_been_submitted, NotificationBannerType.Success);
      })
      .catch(() => addLocalNotificationBanner((tnb) => tnb.a_technical_error_occurred, NotificationBannerType.Error))
      .finally(() => setLoading(false));
  };

  const handleExpressTransferChanged = (e) => setExpressTransfer(e.target.checked);

  const handleTransferMessageChanged = (e) => setTransferMessage(e.target.value);

  const {
    progressBarSize,
    progressBarPercentage,
  } = getProgressBarSizeAndPercentage(files);

  return (
    <div className={styles.container}>
      <DragAndDrop
        onFileUpload={handleAddFiles}
        acceptedFileTypes={ACCEPTED_FILE_TYPES}
        disabled={loading}
        className={styles.dragAndDropContainer}
      />

      <div>
        <Description text={t.description} />
        <ProgressBar
          percentage={progressBarPercentage}
          description={t.format(t.max_upload_size, { progressBarSize })}
          className={styles.progressBarContainer}
        />
        {files.length > 0 && renderFiles(files, handleRemoveFile, loading)}
      </div>

      <div>
        <Switch
          active={expressTransfer}
          onChange={handleExpressTransferChanged}
          disabled={loading}
          label={t.express_transfer}
          className={styles.switchContainer}
        />
        <span>{t.express_transfer_note}</span>
        <Textarea
          value={transferMessage}
          onChange={handleTransferMessageChanged}
          disabled={loading}
          label={t.message}
          placeholder={t.transfer_message}
          className={styles.textareaContainer}
        />
        <Button
          className={styles.submitButton}
          buttonType="action-primary"
          onClick={handleSubmitFiles}
          text={t.submit_invoices}
          isLoading={loading}
          attributes={{ disabled: (loading || files.length <= 0) }}
        />
      </div>
    </div>
  );
};

export default SubmitInvoices;
